import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../util/reactIntl';
import {
  isResendCooldownError,
  getResendCooldownSeconds,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { dismissSignupConfirmation } from '../../ducks/auth.duck';

import { Heading, IconEmailSent, InlineTextButton, IconClose } from '../../components';

import css from './AuthenticationPage.module.css';

// Fallback only; the throttle error normally carries the exact remaining seconds.
const DEFAULT_RESEND_COOLDOWN_SECONDS = 30;

const EmailVerificationInfo = props => {
  const {
    name,
    email,
    onResendVerificationEmail,
    sendVerificationEmailError,
    sendVerificationEmailInProgress,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();

  const [cooldown, setCooldown] = useState(0);
  const [sent, setSent] = useState(false);
  const wasInProgress = useRef(false);

  // Success just confirms the send; only a throttle error starts the countdown.
  useEffect(() => {
    const was = wasInProgress.current;
    wasInProgress.current = sendVerificationEmailInProgress;

    if (sendVerificationEmailInProgress && !was) {
      setSent(false);
      return;
    }
    const justSettled = was && !sendVerificationEmailInProgress;
    if (!justSettled) return;

    if (isResendCooldownError(sendVerificationEmailError)) {
      setCooldown(getResendCooldownSeconds(sendVerificationEmailError) || DEFAULT_RESEND_COOLDOWN_SECONDS);
    } else if (!sendVerificationEmailError) {
      setSent(true);
    }
  }, [sendVerificationEmailInProgress, sendVerificationEmailError]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown(prev => (prev <= 1 ? 0 : prev - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Close leaves the flow for the landing page; "fix it" returns to the
  // prefilled signup form (values held in the parent) to correct the email.
  const onClose = () => {
    dispatch(dismissSignupConfirmation());
    history.push('/');
  };
  const onFixEmail = () => dispatch(dismissSignupConfirmation());

  const resendArea = sendVerificationEmailInProgress ? (
    <FormattedMessage id="AuthenticationPage.sendingEmail" />
  ) : cooldown > 0 ? (
    <FormattedMessage id="AuthenticationPage.resendCooldown" values={{ seconds: cooldown }} />
  ) : (
    <FormattedMessage
      id="AuthenticationPage.resendEmail"
      values={{
        resendEmailLink: (
          <InlineTextButton rootClassName={css.modalHelperLink} onClick={onResendVerificationEmail}>
            <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
          </InlineTextButton>
        ),
      }}
    />
  );

  // A cooldown error is surfaced as the countdown, not as a failure message.
  const showResendError = sendVerificationEmailError && !isResendCooldownError(sendVerificationEmailError);
  const resendErrorId = isTooManyEmailVerificationRequestsError(sendVerificationEmailError)
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return (
    <div className={css.contentVerification}>
      <InlineTextButton rootClassName={css.verifyClose} onClick={onClose}>
        <span className={css.closeText}>
          <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </InlineTextButton>
      <IconEmailSent className={css.modalIcon} />
      <Heading as="h1" rootClassName={css.modalTitle}>
        {name ? (
          <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
        ) : (
          <FormattedMessage id="AuthenticationPage.verifyEmailTitleNoName" />
        )}
      </Heading>
      <p className={css.modalMessage}>
        <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
      </p>
      {showResendError ? (
        <p className={css.error}>
          <FormattedMessage id={resendErrorId} />
        </p>
      ) : null}

      <div className={css.bottomWrapper}>
        {sent && cooldown === 0 && !sendVerificationEmailInProgress ? (
          <p className={css.modalHelperText}>
            <FormattedMessage id="AuthenticationPage.resendSent" />
          </p>
        ) : null}
        <p className={css.modalHelperText}>{resendArea}</p>
        <p className={css.modalHelperText}>
          <FormattedMessage
            id="AuthenticationPage.fixEmail"
            values={{
              fixEmailLink: (
                <InlineTextButton rootClassName={css.modalHelperLink} onClick={onFixEmail}>
                  <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
                </InlineTextButton>
              ),
            }}
          />
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationInfo;
